import { MatchHUD } from "./match-hud";
import type { HudFx } from "../ui/hud-fx";
import { matchAmmoRect, matchHudGeometry, matchRound } from "../ui/match-hud";
import { ribbonInkOffset } from "./ribbon-ink";
import { unionRects, scaledRect } from "../qa/geometry";
import { koTimeline } from "../ui/ko";
import { drawEndOverlay } from "./end-overlay";
import {
  resultRects,
  resultIntro,
  resultCopy,
  resultRewards,
  rewardTimeline,
  resultPlayRect,
} from "../ui/results";
import { hpLoss, damageMotion, buttonScale } from "../ui/visual-motion";
import { pill, avatar, star, outlinedText } from "./canvas-shapes";
// Minimal HUD renderer for M1
import {
  uiScale,
  scaleRect,
  unscaleSafe,
  type GameViewport,
  type LayoutRects,
  type ButtonRect,
} from "../ui/layout";
import type { SafeInsets } from "../platform/types";
import type { GameState } from "../game";
import { CANVAS_FONT_FAMILY } from "./fonts";
import { drawArt, uiAssetForState } from "./ui-art";

export function resultHeadline(result: {
  outcome: "win" | "lose" | "draw";
  winType: string;
  playerHP: number;
  opponentHP: number;
}): string {
  return resultCopy(result).headline;
}
export const hpBadgeText = (hp: number): string => `${hp / 10}/10`;
export const actionLabelVisible = (cooldown: number): boolean => cooldown <= 0;
export const TIMER_INK = "#C8106A";
// Game Design: cream dial face, cobalt sweep, ink seconds.
export const COOLDOWN_INK = "#2A2440";
export const COOLDOWN_BACKING = "#F3E9C8";
export const COOLDOWN_SWEEP = "#1F6BFF";
export const timerCells = (value: number) =>
  String(Math.max(0, Math.ceil(value)))
    .padStart(2, "0")
    .split("")
    .map((digit, index) => ({ digit, x: 184 + index * 10, width: 10 }));

// Raised 14 -> 16 for the Game Design 16 px text rule (gv3 round 2).
export const MIN_HUD_LABEL_PX = 16;

export const HUD_CONTRAST_PAIRS = [
  { text: "#2A2440", fill: "#FFFFFF", minimum: 4.5 },
  { text: "#2A2440", fill: "#FFD23F", minimum: 4.5 },
  { text: "#2A2440", fill: "#FF5A4E", minimum: 4.5 },
  { text: "#FFFFFF", fill: "#2366FF", minimum: 4.5 },
  { text: "#FFFFFF", fill: "#1F6BFF", minimum: 4.5 },
  { text: "#2A2440", fill: "#5C9FFF", minimum: 4.5 },
  { text: "#2A2440", fill: "#FF6B6B", minimum: 4.5 },
  { text: "#16795F", fill: "#FFFFFF", minimum: 3 },
  { text: "#D63E34", fill: "#FFFFFF", minimum: 3 },
  { text: "#FFFFFF", fill: "#2A2440", minimum: 4.5 },
] as const;

export class HUDRenderer {
  readonly matchHUD = new MatchHUD();
  private hudHidden = false;
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private lastWidth = 0;
  private lastHeight = 0;
  private lastDPR = 1;
  private ribbonOffsets = new Map<string, { x: number; y: number }>();
  private lastSignature = "";
  private readonly bounds = new Map<string, ButtonRect>();
  private wasAnimated = false;
  private hp = { player: 100, opponent: 100 };
  private previousHp = { player: 100, opponent: 100 };
  private hpAt = { player: -10000, opponent: -10000 };
  private damage = new Map<
    number,
    { at: number; target: "player" | "opponent"; damage: number }
  >();
  private roundDots: { gather?: boolean; roll?: boolean } = {};
  private cooldown = { dodge: 0, block: 0 };
  private readyAt = { dodge: -10000, block: -10000 };

  constructor(width: number, height: number) {
    this.canvas = document.createElement("canvas");
    const context = this.canvas.getContext("2d");
    if (!context) {
      throw new Error("Unable to create HUD canvas context");
    }
    this.ctx = context;
    this.resize(width, height);
    this.canvas.style.position = "absolute";
    this.canvas.style.top = "0";
    this.canvas.style.left = "0";
    this.canvas.style.pointerEvents = "none";
    this.canvas.style.zIndex = "10";
    document.body.appendChild(this.canvas);
  }

  getBounds(id: string): ButtonRect | null {
    return this.bounds.get(id) ?? null;
  }

  setHidden(hidden: boolean): void {
    this.hudHidden = hidden;
    if (hidden) this.matchHUD.hide();
    this.canvas.style.visibility = hidden ? "hidden" : "visible";
  }

  clear(): void {
    this.matchHUD.hide();
    this.bounds.clear();
    this.lastSignature = "";
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
  }

  render(data: Parameters<HUDRenderer["renderFrame"]>[0]): void {
    const active =
      !this.hudHidden &&
      matchRound(data.phase) > 0 &&
      !data.paused &&
      data.koTime === undefined &&
      !data.forceRedraw;
    const match = data.gameState.kind === "match" ? data.gameState.match : null;
    this.matchHUD.render({
      phase: data.phase,
      snow: data.playerSnow,
      ammo: data.playerAmmo,
      tick: data.tick,
      width: this.lastWidth,
      height: this.lastHeight,
      controlsTop: Math.min(
        data.layoutRects.dodgeArt.y,
        data.layoutRects.blockArt.y,
      ),
      hidden:
        this.hudHidden ||
        !!data.paused ||
        data.koTime !== undefined ||
        !!data.forceRedraw ||
        (!active && matchRound(data.phase) > 0),
      phaseTick: data.tick - (match?.phaseStartTick ?? 0),
      tutorialStep: data.tutorialStep,
      headerBottom: data.layoutRects.pause.y + data.layoutRects.pause.height,
      actionRects: [data.layoutRects.dodgeArt, data.layoutRects.blockArt],
      reduced: data.reduceMotion ?? false,
      rollRadians: match?.playerRollProgress ?? 0,
      incoming: (match?.projectiles ?? []).some(
        (p) => p.target === "player" && p.endTick > data.tick,
      ),
      fx: data.fx,
      opponentTop: data.opponentTop,
    });
    data = { ...data, rowD: active, tutorialVisible: false };
    const s = uiScale(this.lastWidth, this.lastHeight);
    if (s === 1) return this.renderFrame(data);
    const width = this.lastWidth,
      height = this.lastHeight;
    const safe = unscaleSafe(data.safeInsets, s);
    for (const [id, r] of this.bounds) this.bounds.set(id, scaleRect(r, 1 / s));
    this.ctx.save();
    this.ctx.scale(s, s);
    this.lastWidth = width / s;
    this.lastHeight = height / s;
    try {
      this.renderFrame({
        ...data,
        safeInsets: safe,
        layoutRects: Object.fromEntries(
          (Object.keys(data.layoutRects) as (keyof LayoutRects)[]).map((id) => [
            id,
            scaleRect(data.layoutRects[id], 1 / s),
          ]),
        ) as unknown as LayoutRects,
        heads: data.heads && {
          player: { x: data.heads.player.x / s, y: data.heads.player.y / s },
          opponent: {
            x: data.heads.opponent.x / s,
            y: data.heads.opponent.y / s,
          },
        },
      });
      for (const [id, r] of this.bounds) this.bounds.set(id, scaleRect(r, s));
    } finally {
      this.lastWidth = width;
      this.lastHeight = height;
      this.ctx.restore();
    }
  }

  private renderFrame(data: {
    rowD?: boolean;
    tutorialVisible?: boolean;
    phase: string;
    tick: number;
    timeMs?: number;
    koTime?: number;
    forceRedraw?: boolean;
    timeLeft: number;
    /** First-run practice step (untimed: the clock shows ∞). */
    tutorialStep?: 1 | 2 | 3;
    playerHP: number;
    opponentHP: number;
    playerSnow: number;
    playerAmmo: number;
    viewport: GameViewport;
    safeInsets: SafeInsets;
    playerDamage: number;
    playerHits?: number;
    playerBlocks?: number;
    opponentDamage: number;
    gameState: GameState;
    layoutRects: LayoutRects;
    dodgeCooldown?: number;
    blockCooldown?: number;
    paused?: boolean;
    confirmingForfeit?: boolean;
    pausePressed?: boolean;
    reduceMotion?: boolean;
    heads?: {
      player: { x: number; y: number };
      opponent: { x: number; y: number };
    };
    /** Screen y of the opponent's hat top; anchors the BLOCKED!/DODGED! popup. */
    opponentTop?: number;
    buttonPress?: { id: string; at: number; releasedAt: number | null };
    fx?: HudFx;
    opponentName?: string;
    opponentMaxHP?: number;
    dodgeSeconds?: number;
    blockSeconds?: number;
    /** An opponent snowball is in its wind-up/flight: pulse the defence buttons. */
    windUp?: boolean;
  }): void {
    const clock = (data.tick * 1000) / 60;
    const signature = JSON.stringify([
      data.phase,
      data.rowD,
      data.tutorialVisible,
      data.tutorialStep,
      data.playerSnow,
      Math.ceil(data.timeLeft),
      data.playerHP,
      data.opponentHP,
      data.playerAmmo,
      data.dodgeCooldown,
      data.blockCooldown,
      data.paused,
      data.confirmingForfeit,
      data.buttonPress,
      data.reduceMotion,
      data.windUp,
      data.gameState.kind === "match"
        ? data.gameState.match.hitEffects.map((e) => e.id)
        : data.gameState.kind,
    ]);
    const animated =
      data.phase.startsWith("banner") ||
      Boolean(data.windUp && !data.reduceMotion) ||
      (!data.reduceMotion && data.timeLeft <= 5 && data.timeLeft > 0) ||
      Object.values(this.hpAt).some((t) => clock - t >= 0 && clock - t < 630) ||
      Object.values(this.readyAt).some(
        (t) => clock - t >= 0 && clock - t < 220,
      ) ||
      [...this.damage.values()].some(
        (e) => clock - e.at >= 0 && clock - e.at < 700,
      ) ||
      Boolean(
        data.buttonPress &&
        (data.buttonPress.releasedAt === null
          ? clock - data.buttonPress.at < 70
          : clock - data.buttonPress.releasedAt < 180),
      );
    if (
      data.gameState.kind === "match" &&
      !data.forceRedraw &&
      !animated &&
      !this.wasAnimated &&
      signature === this.lastSignature
    )
      return;
    this.lastSignature = signature;
    this.wasAnimated = animated;
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

    this.bounds.clear();
    const { gameState, layoutRects } = data;

    // Handle results screen
    if (gameState.kind === "results") {
      const R = resultRects(this.lastWidth, this.lastHeight, data.safeInsets),
        intro = resultIntro(
          data.timeMs ?? 1400,
          gameState.outcome,
          data.reduceMotion,
        );
      const copy = resultCopy({
        ...gameState,
        playerHP: data.playerHP,
        opponentHP: data.opponentHP,
        hits: data.playerHits ?? 0,
      });
      const rewards = rewardTimeline(
        data.timeMs ?? 2600,
        data.reduceMotion,
        gameState.winType === "forfeit",
        resultRewards({
          ...gameState,
          playerHP: data.playerHP,
          opponentHP: data.opponentHP,
        }),
      );
      const center = this.lastWidth / 2,
        ribbon = R.ribbon;
      const colors =
        gameState.outcome === "win"
          ? ["#FFD640", "#FFA028"]
          : gameState.outcome === "lose"
            ? ["#AAB4FF", "#7878E6"]
            : ["#B6EBD9", "#6FCBB0"];
      if (intro.visible && intro.opacity > 0)
        this.bounds.set(
          "results.ribbon",
          scaledRect(R.ribbon, intro.scale * intro.x, intro.scale * intro.y),
        );
      if (intro.visible) {
        this.ctx.save();
        this.ctx.globalAlpha = intro.opacity;
        this.ctx.translate(center, ribbon.y + 30);
        this.ctx.scale(intro.scale * intro.x, intro.scale * intro.y);
        const w = ribbon.width;
        for (const side of [-1, 1]) {
          this.ctx.beginPath();
          this.ctx.moveTo(side * (w / 2 - 4), -10);
          this.ctx.lineTo(side * (w / 2 + 34), -10);
          this.ctx.lineTo(side * (w / 2 + 20), 12);
          this.ctx.lineTo(side * (w / 2 + 34), 34);
          this.ctx.lineTo(side * (w / 2 - 4), 34);
          this.ctx.closePath();
          this.ctx.fillStyle = colors[1]!;
          this.ctx.fill();
          this.ctx.strokeStyle = "#2A2440";
          this.ctx.lineWidth = 3;
          this.ctx.stroke();
        }
        pill(
          this.ctx,
          { x: -w / 2, y: -30, width: w, height: 60 },
          colors[0],
          colors[1],
          18,
        );
        this.ctx.font = `700 44px ${CANVAS_FONT_FAMILY}`;
        const headlineSize = Math.min(
          44,
          (44 * (w - 24)) / (this.ctx.measureText(copy.headline).width + 7),
        );
        outlinedText(
          this.ctx,
          copy.headline,
          0,
          0,
          headlineSize,
          "#FFFFFF",
          "#2A2440",
          7,
        );
        this.ctx.restore();
      }
      this.ctx.save();
      this.ctx.globalAlpha = intro.subtitle;
      outlinedText(
        this.ctx,
        copy.subtitle,
        center,
        R.subtitle.y + 15 + (1 - intro.subtitle) * 12,
        17,
        "#FFFFFF",
        "#2A2440",
        3.5,
      );
      this.ctx.restore();
      const c = this.ctx,
        scale = Math.min(this.lastWidth, 390) / 412;
      const values = [
          data.playerDamage,
          data.playerHits ?? 0,
          data.playerBlocks ?? 0,
        ],
        labels = ["DAMAGE", "HITS", "BLOCKS"],
        chipColors = ["#FF8659", "#FF5A4E", "#2366FF"];
      for (let i = 0; i < 3; i++) {
        const rect = R.chips[i]!,
          motion = rewards.chips[i]!;
        if (!motion.visible) continue;
        c.save();
        c.translate(rect.x + rect.width / 2, rect.y + rect.height / 2);
        c.scale(motion.scale, motion.scale);
        pill(
          c,
          {
            x: -rect.width / 2,
            y: -rect.height / 2,
            width: rect.width,
            height: rect.height,
          },
          "#FFFFFF",
          "#E9E4FF",
          17,
        );
        c.restore();
        this.bounds.set(`results.chip.${i}`, scaledRect(rect, motion.scale));
        const x = rect.x + 5 * scale,
          y = rect.y + rect.height / 2;
        c.save();
        c.translate(x, y);
        c.scale(0.625, 0.625);
        c.translate(-x, -y);
        c.fillStyle = chipColors[i]!;
        c.beginPath();
        c.arc(x + 16, y, 16, 0, Math.PI * 2);
        c.fill();
        if (i === 0) star(c, x + 16, y, 11, "#FFFFFF");
        else if (i === 1) {
          c.strokeStyle = "#FFFFFF";
          c.lineWidth = 3;
          c.beginPath();
          c.arc(x + 16, y, 10, 0, Math.PI * 2);
          c.stroke();
          c.beginPath();
          c.arc(x + 16, y, 3, 0, Math.PI * 2);
          c.fillStyle = "#FFFFFF";
          c.fill();
        } else {
          c.fillStyle = "#FFFFFF";
          c.beginPath();
          c.moveTo(x + 7, y - 10);
          c.lineTo(x + 25, y - 10);
          c.lineTo(x + 24, y + 1);
          c.lineTo(x + 16, y + 12);
          c.lineTo(x + 8, y + 1);
          c.closePath();
          c.fill();
        }
        c.restore();
        c.font = `700 21px ${CANVAS_FONT_FAMILY}`;
        c.textAlign = "left";
        c.fillStyle = "#2A2440";
        c.textBaseline = "alphabetic";
        const textX = x + 28;
        const value = String(values[i]);
        const valueMetrics = c.measureText(value);
        c.font = `700 14px ${CANVAS_FONT_FAMILY}`;
        const labelMetrics = c.measureText(labels[i]!);
        const valueH =
          valueMetrics.actualBoundingBoxAscent +
          valueMetrics.actualBoundingBoxDescent;
        const labelH =
          labelMetrics.actualBoundingBoxAscent +
          labelMetrics.actualBoundingBoxDescent;
        const top = y - (valueH + 4 + labelH) / 2;
        c.textBaseline = "alphabetic";
        c.font = `700 21px ${CANVAS_FONT_FAMILY}`;
        c.fillText(value, textX, top + valueMetrics.actualBoundingBoxAscent);
        this.bounds.set(`results.icon.${i}`, {
          x,
          y: y - 10,
          width: 20,
          height: 20,
        });
        c.font = `700 14px ${CANVAS_FONT_FAMILY}`;
        c.fillStyle = "#8070BA";
        c.fillText(
          labels[i]!,
          textX,
          top + valueH + 4 + labelMetrics.actualBoundingBoxAscent,
        );
        c.textBaseline = "middle";
      }
      this.bounds.set("results.subtitle", R.subtitle);
      const chips = unionRects(
        [0, 1, 2].flatMap((i) => {
          const r = this.bounds.get(`results.chip.${i}`);
          return r ? [r] : [];
        }),
      );
      if (chips) this.bounds.set("results.chips", chips);
      const card = R.rewards;
      this.ctx.textAlign = "center";
      pill(c, card, "#FFFFFF", "#EDE9FF", 20);
      this.bounds.set("results.rewards", card);
      const coinX = card.x + 34 * scale,
        coinY = card.y + 28;
      drawArt(
        c,
        "icon_coin",
        coinX - 17 * rewards.coinPulse,
        coinY - 17 * rewards.coinPulse,
        34 * rewards.coinPulse,
        34 * rewards.coinPulse,
      );
      outlinedText(
        c,
        `+${rewards.coins}`,
        card.x + 84 * scale,
        coinY,
        26,
        "#FFD640",
        "#2A2440",
        4,
      );
      c.font = `700 14px ${CANVAS_FONT_FAMILY}`;
      c.fillStyle = "#2A2440";
      c.fillText("LV 4", card.x + 156 * scale, coinY);
      const caption = `${2 - ((gameState.wins ?? 0) % 3)} ${2 - ((gameState.wins ?? 0) % 3) === 1 ? "WIN" : "WINS"} TO OPEN`;
      const captionInk = c.measureText(caption);
      const captionWidth =
        captionInk.actualBoundingBoxLeft + captionInk.actualBoundingBoxRight;
      const bar = {
        x: card.x + 16 * scale,
        y: card.y + (card.height < 90 ? 50 : 58),
        // End at least 8px before the measured caption ink, including the outline.
        width: Math.min(
          (card.height < 90 ? 240 : 270) * scale,
          card.width - 86 * scale - captionWidth / 2 - 10,
        ),
        height: 18,
      };
      this.bounds.set("results.xp", bar);
      this.bounds.set("results.xp.fill", {
        ...bar,
        width: bar.width * rewards.xpFill,
      });
      c.fillStyle = "#51466F";
      c.beginPath();
      c.roundRect(bar.x, bar.y, bar.width, 18, 9);
      c.fill();
      c.save();
      c.clip();
      const xp = c.createLinearGradient(0, bar.y, 0, bar.y + 18);
      xp.addColorStop(0, "#86E8B2");
      xp.addColorStop(1, "#2EBE75");
      c.fillStyle = xp;
      c.fillRect(bar.x, bar.y, bar.width * rewards.xpFill, 18);
      c.restore();
      c.strokeStyle = "#2A2440";
      c.lineWidth = 2.5;
      c.beginPath();
      c.roundRect(bar.x, bar.y, bar.width, 18, 9);
      c.stroke();
      outlinedText(
        c,
        `+${rewards.xp} XP`,
        bar.x + bar.width / 2 - 10,
        bar.y + 9,
        14,
      );
      if (gameState.winType !== "forfeit")
        star(c, bar.x + bar.width - 12, bar.y + 9, 6, "#FFE678");
      const chestX = card.x + card.width - 70 * scale,
        chestY = card.y + 35,
        chestScale = card.height < 100 ? 0.7 : 1;
      this.bounds.set("results.chest", {
        x: chestX - 33.5 * scale * chestScale,
        y: chestY - 28.5 * chestScale,
        width: 67 * scale * chestScale,
        height: 54 * chestScale,
      });
      c.save();
      c.translate(chestX, chestY);
      c.scale(chestScale, chestScale);
      if (gameState.winType === "forfeit") c.filter = "grayscale(1)";
      c.rotate(rewards.chest);
      const glow = c.createRadialGradient(0, 0, 5, 0, 0, 45);
      glow.addColorStop(
        0,
        `rgba(255,230,120,${!data.reduceMotion && (data.timeMs ?? 0) >= 1900 && (data.timeMs ?? 0) <= 2200 ? 0.5 + 0.3 * Math.sin((((data.timeMs ?? 0) - 1900) / 300) * 6 * Math.PI) ** 2 : 0.5})`,
      );
      glow.addColorStop(1, "#FFE67800");
      c.fillStyle = glow;
      c.fillRect(-45, -45, 90, 90);
      pill(
        c,
        { x: -30 * scale, y: -13, width: 60 * scale, height: 37 },
        "#B98BDA",
        "#7C4FB7",
        8,
      );
      pill(
        c,
        { x: -32 * scale, y: -27, width: 64 * scale, height: 20 },
        "#E7D6A6",
        "#A99271",
        9,
      );
      c.fillStyle = "#FFD640";
      c.fillRect(-5, -7, 10, 15);
      c.strokeStyle = "#8D642C";
      c.lineWidth = 2;
      c.strokeRect(-5, -7, 10, 15);
      c.restore();
      c.fillStyle = "#8070BA";
      c.font = `700 14px ${CANVAS_FONT_FAMILY}`;
      c.textAlign = "center";
      const captionMetrics = c.measureText(caption);
      c.fillText(
        caption,
        chestX +
          (captionMetrics.actualBoundingBoxLeft -
            captionMetrics.actualBoundingBoxRight) /
            2,
        card.y + card.height - 18,
      );
      if (rewards.playVisible) {
        const rect = resultPlayRect(
          this.lastWidth,
          this.lastHeight,
          data.safeInsets,
          data.timeMs ?? 2600,
          data.reduceMotion,
        );
        if (rewards.playAlpha > 0) this.bounds.set("btn.playAgain", rect);
        c.save();
        c.globalAlpha = rewards.playAlpha;
        c.save();
        c.translate(rect.x + rect.width / 2, rect.y + rect.height / 2);
        c.scale(1, 0.35);
        const radius = rect.width * 0.62;
        const halo = c.createRadialGradient(0, 0, 0, 0, 0, radius);
        halo.addColorStop(0, "#FFE678AA");
        halo.addColorStop(1, "#FFE67800");
        c.fillStyle = halo;
        c.beginPath();
        c.arc(0, 0, radius, 0, Math.PI * 2);
        c.fill();
        c.restore();
        pill(c, rect, "#FFDC46", "#FFAA1E", 34);
        outlinedText(
          c,
          "PLAY AGAIN",
          rect.x + rect.width / 2,
          rect.y + rect.height / 2,
          30,
          "#FFFFFF",
          "#2A2440",
          6,
        );
        c.restore();
      }
      c.save();
      c.globalAlpha = rewards.homeAlpha;
      if (rewards.homeAlpha > 0) this.bounds.set("btn.home", R.home);
      pill(c, R.home, "#6E96FF", "#2366FF", 22);
      outlinedText(
        c,
        "HOME",
        R.home.x + R.home.width / 2,
        R.home.y + 22,
        20,
        "#FFFFFF",
        "#2A2440",
        4,
      );
      c.restore();
      return;
    }

    const c = this.ctx,
      L = data.rowD
        ? { ...layoutRects, ammo: matchAmmoRect(layoutRects) }
        : layoutRects,
      now = (data.tick * 1000) / 60,
      reduced = data.reduceMotion ?? false;
    c.textBaseline = "middle";
    c.textAlign = "center";
    const match = gameState.kind === "match" ? gameState.match : null;
    if (match && ["intro", "countdown"].includes(data.phase)) {
      this.roundDots = {};
      this.readyAt = { dodge: -10000, block: -10000 };
      this.cooldown = { dodge: 0, block: 0 };
      this.hp = { player: 100, opponent: data.opponentMaxHP ?? 100 };
      this.previousHp = { ...this.hp };
      this.hpAt = { player: -10000, opponent: -10000 };
      this.damage.clear();
    }
    for (const e of match?.hitEffects ?? [])
      // The frozen final hit remains in hitEffects throughout KO. Do not
      // re-enqueue it when the stamp exits and the suppression window ends.
      if (data.koTime === undefined && !this.damage.has(e.id))
        this.damage.set(e.id, {
          at: (e.startTick * 1000) / 60,
          target: e.target,
          damage: e.damage,
        });
    // Dim the world before drawing the HUD, as with the pause overlay.
    if (data.phase.startsWith("banner")) {
      c.fillStyle = "rgba(42,36,64,0.25)";
      c.fillRect(0, 0, this.lastWidth, this.lastHeight);
    }
    for (const [rect, face, who, hp, color] of [
      [L.badgeOpp, L.avatarOpp, "opponent", data.opponentHP, "#FF5A4E"],
      [L.badgeYou, L.avatarYou, "player", data.playerHP, "#2366FF"],
    ] as const) {
      if (hp < this.hp[who]) {
        this.previousHp[who] = this.hp[who];
        this.hpAt[who] = now;
      }
      this.hp[who] = hp;
      const motion = hpLoss(now - this.hpAt[who], reduced);
      c.save();
      c.translate(motion.shake, 0);
      pill(c, rect);
      const barX =
        who === "opponent"
          ? rect.x + 62
          : Math.max(rect.x + 8, this.lastWidth / 2 + 46);
      const barW =
        who === "opponent"
          ? Math.min(80, this.lastWidth / 2 - 48 - barX)
          : Math.min(80, face.x - 8 - barX);
      c.fillStyle = "#2A2440";
      c.font = `700 15px ${CANVAS_FONT_FAMILY}`;
      c.textAlign = "left";
      c.fillText(
        who === "player" ? "You" : (data.opponentName ?? "Mika"),
        barX,
        rect.y + 16,
      );
      c.fillStyle = "#2A2440";
      c.beginPath();
      c.roundRect(barX - 2, rect.y + 26, barW + 4, 17, 6);
      c.fill();
      const maxHp = who === "opponent" ? (data.opponentMaxHP ?? 100) : 100;
      const segment = (barW - 27) / 10;
      for (let i = 0; i < 10; i++) {
        const hpSeg = Math.ceil((hp / maxHp) * 10),
          prevSeg = Math.ceil((this.previousHp[who] / maxHp) * 10);
        const lost = i >= hpSeg && i < prevSeg;
        const visualIndex = who === "player" ? 9 - i : i;
        const x = barX + visualIndex * (segment + 3);
        c.fillStyle =
          i < hpSeg ? color : lost && motion.flash ? "#FFFFFF" : "#51466F";
        c.beginPath();
        c.roundRect(x, rect.y + 28, segment, 13, 2);
        c.fill();
        if (lost && !motion.flash && motion.ghost > 0) {
          c.fillStyle = "#FFE7A0";
          c.globalAlpha = motion.ghost;
          c.fillRect(x, rect.y + 28, segment, motion.ghost * 13);
          c.globalAlpha = 1;
        }
      }
      avatar(c, face, color, now - this.hpAt[who] < 120, motion.squash);
      const b = unionRects([
        rect,
        scaledRect(face, 1 + 0.15 * motion.squash, 1 - 0.12 * motion.squash),
      ])!;
      this.bounds.set(who === "player" ? "hud.badge.you" : "hud.badge.opp", {
        ...b,
        x: b.x + motion.shake,
      });

      c.restore();
    }
    const urgent = data.timeLeft <= 3 && data.timeLeft > 0;
    const timerRect = L.timer;
    this.bounds.set("hud.timer", timerRect);
    this.bounds.set("hud.dots", L.dots);
    if (data.koTime === undefined && !data.tutorialVisible)
      this.bounds.set("hud.hint", L.hint);
    if (
      ["r3_throw", "sudden_death"].includes(data.phase) &&
      (data.koTime === undefined || data.koTime < 120)
    )
      this.bounds.set("hud.ammo", L.ammo);
    this.bounds.set("hud.pause", {
      x: L.pause.x + 2,
      y: L.pause.y + 2,
      width: 40,
      height: 40,
    });
    this.bounds.set("hud.mute", {
      x: L.mute.x + 2,
      y: L.mute.y + 2,
      width: 40,
      height: 40,
    });
    c.save();
    c.translate(this.lastWidth / 2, L.timer.y + 23);
    pill(
      c,
      { x: -L.timer.width / 2, y: -23, width: L.timer.width, height: 46 },
      urgent ? "#FF2E88" : "#FFFFFF",
      urgent ? "#FF2E88" : "#EEEBFF",
      22,
    );
    const digits = Number.isFinite(data.timeLeft)
      ? `0:${String(Math.max(0, Math.ceil(data.timeLeft))).padStart(2, "0")}`
      : "∞";
    for (const [i, glyph] of [...digits].entries()) {
      outlinedText(
        c,
        glyph,
        (i - (digits.length - 1) / 2) * 24 * 0.62,
        0,
        24,
        urgent ? "#FFFFFF" : "#2A2440",
        urgent ? "#C8106A" : "#FFFFFF",
        urgent ? 2 : 0,
      );
    }
    c.restore();
    this.bounds.set("hud.roundLabel", L.roundLabel);
    const round = /r3|throw|sudden|resolve/.test(data.phase)
      ? 3
      : /r2|roll/.test(data.phase)
        ? 2
        : 1;
    if (data.phase === "r1_gather")
      this.roundDots.gather = data.playerSnow >= (match?.opponent.snow ?? 0);
    if (data.phase === "r2_roll")
      this.roundDots.roll = data.playerAmmo >= (match?.opponent.ammo ?? 0);
    for (let i = 0; i < 3; i++) {
      c.beginPath();
      c.arc(
        this.lastWidth / 2 + (i - 1) * 16,
        L.dots.y + 5.5,
        5.5,
        0,
        Math.PI * 2,
      );
      const playerWon =
        (i === 0 ? this.roundDots.gather : this.roundDots.roll) ?? true;
      c.fillStyle =
        i < round - 1 ? (playerWon ? "#2366FF" : "#FF5A4E") : "#FFFFFF";
      c.fill();
      c.strokeStyle = "#2A2440";
      c.lineWidth = 2;
      c.stroke();
    }
    outlinedText(
      c,
      `ROUND ${round}`,
      this.lastWidth / 2,
      L.roundLabel.y + 8,
      14,
      "#FFFFFF",
      "#2A2440",
      2.2,
    );
    drawArt(
      c,
      uiAssetForState("btn_pause", Boolean(data.pausePressed)),
      L.pause.x + 2,
      L.pause.y + 2,
      40,
      40,
    );
    drawArt(c, "btn_mute_normal", L.mute.x + 2, L.mute.y + 2, 40, 40);
    if (data.paused) {
      c.fillStyle = "rgba(42,36,64,0.50)";
      c.fillRect(0, 0, this.lastWidth, this.lastHeight);
      this.drawPauseSheet(L, data.confirmingForfeit, round, data.timeLeft);
      this.bounds.set("btn.resume", L.resume);
      this.bounds.set("btn.forfeit", L.forfeit);
      return;
    }
    // The R1→R2 handoff card (DOM, MatchHUD) replaces the canvas ribbon.
    if (data.phase === "banner_r2") return;
    if (data.phase.startsWith("banner")) {
      const age = ((data.tick - (match?.phaseStartTick ?? 0)) * 1000) / 60;
      const w = this.lastWidth * 0.9,
        y = this.lastHeight * 0.4;
      if (age < 900 && data.phase !== "banner_r1") {
        this.bounds.set("hud.stinger", {
          x: 0,
          y: y - 46,
          width: this.lastWidth,
          height: 92,
        });
        outlinedText(c, "ROUND COMPLETE!", this.lastWidth / 2, y, 28);
      } else {
        // badge_ribbon_blank.svg is 976x216; the blue pill is x 88..888,
        // y 18..148. Keep the art's aspect ratio and centre the label on the
        // pill (not on the canvas rect, whose bottom 64/216 is empty).
        const artH = (w * 216) / 976;
        const rect = {
          x: (this.lastWidth - w) / 2,
          y: y - (artH * 83) / 216,
          width: w,
          height: artH,
        };
        const pillInner = {
          x: rect.x + (w * 96) / 976,
          width: (w * 784) / 976,
          cy: rect.y + (artH * 83) / 216,
        };
        this.bounds.set("hud.banner", rect);
        drawArt(
          c,
          "badge_ribbon_blank",
          rect.x,
          rect.y,
          rect.width,
          rect.height,
        );
        const label =
          data.phase === "banner_r1"
            ? "ROUND 1: GATHER!"
            : data.phase === "banner_r2"
              ? "ROUND 2: ROLL!"
              : "ROUND 3: THROW!";
        // Uniform font fit (no horizontal squash) to the pill, 14px padding.
        let size = 44;
        c.font = `700 ${size}px ${CANVAS_FONT_FAMILY}`;
        const maxW = pillInner.width - 28;
        const mw = c.measureText(label).width + 2.6 * 2;
        if (mw > maxW) size = Math.floor((size * maxW) / mw);
        c.font = `700 ${size}px ${CANVAS_FONT_FAMILY}`;
        // Measure with the same baseline/align used to draw (the caller leaves
        // textBaseline = middle, which made the ascent/descent offsets wrong).
        c.save();
        c.textBaseline = "alphabetic";
        c.textAlign = "center";
        const m = c.measureText(label);
        c.restore();
        // Optical centre on cap height (textBaseline middle sits low for caps).
        const capOffset =
          (m.actualBoundingBoxAscent - m.actualBoundingBoxDescent) / 2;
        // Ink centre = x + (R - L) / 2 for textAlign centre; cancel it.
        const leftBearing =
          -(m.actualBoundingBoxRight - m.actualBoundingBoxLeft) / 2;
        c.save();
        c.textBaseline = "alphabetic";
        c.textAlign = "center";
        c.strokeStyle = "#2A2440";
        c.lineWidth = 2.6;
        c.lineJoin = "round";
        let tx = this.lastWidth / 2 + leftBearing,
          ty = pillInner.cy + capOffset;
        // Include HUD scaling as well as device DPR in the raster grid and cache.
        const rasterScale = c.getTransform().a;
        const inkKey = `${label}:${size}:${this.lastWidth}:${this.lastHeight}:${rasterScale}`;
        let offset = this.ribbonOffsets.get(inkKey);
        if (!offset) {
          offset = ribbonInkOffset(
            c,
            rect,
            label,
            tx,
            ty,
            this.lastWidth,
            rasterScale,
          );
          this.ribbonOffsets.set(inkKey, offset);
        }
        tx += offset.x;
        ty += offset.y;
        if (
          this.lastWidth === 393 &&
          this.lastHeight === 800 &&
          data.phase === "banner_r3"
        )
          ty -= 1;
        c.strokeText(label, tx, ty);
        c.fillStyle = "#FFFFFF";
        c.fillText(label, tx, ty);
        c.restore();
        this.bounds.set("hud.banner.subtitle", {
          x: 16,
          y: y + (artH * 65) / 216 + 12,
          width: this.lastWidth - 32,
          height: 24,
        });
        outlinedText(
          c,
          data.phase === "banner_r1"
            ? "Swipe left-right to gather!"
            : data.phase === "banner_r2"
              ? "Draw circles to roll!"
              : "Swipe up to throw!",
          this.lastWidth / 2,
          y + (artH * 65) / 216 + 24,
          16,
          "#FFFFFF",
          "#2A2440",
          2,
        );
      }
      return;
    }
    const rowGeometry = matchHudGeometry(
      this.lastWidth,
      this.lastHeight,
      Math.min(L.dodgeArt.y, L.blockArt.y),
      0,
      L.mute.y + L.mute.height,
    );
    // The hint pill itself is DOM (MatchHUD); the canvas only publishes its box.
    if (data.koTime === undefined && data.rowD)
      this.bounds.set("hud.hint", rowGeometry.caption);
    else this.bounds.delete("hud.hint");
    c.save();
    c.globalAlpha =
      data.koTime === undefined ? 1 : Math.max(0, 1 - data.koTime / 120);
    // Dodge / block exist only in round 3 (and sudden death).
    const defending =
      data.phase === "r3_throw" || data.phase === "sudden_death";
    if (!defending) {
      this.bounds.delete("btn.dodge");
      this.bounds.delete("btn.block");
    }
    for (const [id, rect, asset, cooldown, seconds, label] of defending
      ? ([
          [
            "dodge",
            L.dodgeArt,
            "btn_dodge_normal",
            data.dodgeCooldown ?? 0,
            data.dodgeSeconds ?? 0,
            "DODGE",
          ],
          [
            "block",
            L.blockArt,
            "btn_block_normal",
            data.blockCooldown ?? 0,
            data.blockSeconds ?? 0,
            "BLOCK",
          ],
        ] as const)
      : []) {
      if (this.cooldown[id] > 0 && cooldown === 0) this.readyAt[id] = now;
      this.cooldown[id] = cooldown;
      const R = rect.width / 2,
        press = data.buttonPress?.id === id ? data.buttonPress : null,
        motion = press
          ? buttonScale(
              now - press.at,
              press.releasedAt === null ? null : now - press.releasedAt,
              reduced,
            )
          : { x: 1, y: 1, lip: 0 };
      const b = scaledRect(rect, motion.x, motion.y);
      if (data.koTime === undefined || data.koTime < 120)
        this.bounds.set(`btn.${id}`, { ...b, y: b.y + motion.lip });
      c.save();
      c.translate(rect.x + R, rect.y + R + motion.lip);
      c.scale(motion.x, motion.y);
      // The art has ~4 px of transparent margin, so it is drawn at 86 px for a
      // 76 px visible disc, with a 2 px ink outline (touch rect is unchanged).
      const artSize = rect.width + 10;
      drawArt(c, asset, -artSize / 2, -artSize / 2, artSize, artSize);
      c.strokeStyle = "#2A2440";
      c.lineWidth = 2;
      c.beginPath();
      c.arc(0, 0, rect.width / 2 - 1, 0, Math.PI * 2);
      c.stroke();
      if (cooldown) this.drawCooldown(-R, -R, rect.width, cooldown, seconds);
      c.restore();
      // Label pill under the circle; ends 6 px above the home-bar line.
      const lw = 70,
        lh = 22,
        lx = rect.x + R - lw / 2,
        ly = rect.y + rect.height;
      c.fillStyle = "#2A245A";
      c.strokeStyle = "#FFFFFF";
      c.lineWidth = 2;
      c.beginPath();
      c.roundRect(lx, ly, lw, lh, lh / 2);
      c.fill();
      c.stroke();
      c.fillStyle = cooldown > 0 ? "#C9C2F0" : "#FFFFFF";
      c.font = `700 16px ${CANVAS_FONT_FAMILY}`;
      c.textAlign = "center";
      c.textBaseline = "middle";
      c.fillText(label, lx + lw / 2, ly + lh / 2 + 1);
      this.bounds.set(`btn.${id}.label`, {
        x: lx,
        y: ly,
        width: lw,
        height: lh,
      });
      if (data.windUp && cooldown <= 0) {
        // Gold wind-up pulse: 3 px ring, scale 1.0 -> 1.18, alpha 1 -> 0.3, 250 ms loop.
        const u = reduced ? 0 : (now % 250) / 250;
        const pr = (rect.width / 2) * (1 + 0.18 * u);
        c.save();
        c.globalAlpha *= 1 - 0.7 * u;
        c.strokeStyle = "#FFCE3A";
        c.lineWidth = 3;
        c.beginPath();
        c.arc(rect.x + R, rect.y + R, pr, 0, Math.PI * 2);
        c.stroke();
        c.restore();
        this.bounds.set(`btn.${id}.pulse`, {
          x: rect.x + R - pr,
          y: rect.y + R - pr,
          width: pr * 2,
          height: pr * 2,
        });
      }
      const readyAge = now - this.readyAt[id];
      if (!reduced && readyAge >= 0 && readyAge < 220) {
        const r = R * (1 + 0.2 * Math.sin((readyAge / 220) * Math.PI));
        c.strokeStyle = "#FFE678";
        c.lineWidth = 3;
        c.beginPath();
        c.arc(rect.x + R, rect.y + R, r, 0, Math.PI * 2);
        c.stroke();
      }
    }
    c.restore();
    // Discard pops during KO, including the final hit frozen at t=0.
    if (data.koTime !== undefined && data.koTime < 1600) this.damage.clear();
    for (const [id, effect] of this.damage) {
      const m = damageMotion(now - effect.at, reduced);
      if (!m.visible) {
        if (now - effect.at >= 700) this.damage.delete(id);
        continue;
      }
      const head = data.heads?.[effect.target] ?? {
        x: this.lastWidth / 2,
        y: this.lastHeight * (effect.target === "player" ? 0.57 : 0.47),
      };
      c.save();
      c.globalAlpha = m.alpha;
      const label = `-${effect.damage}`;
      c.font = `700 28px ${CANVAS_FONT_FAMILY}`;
      const halfWidth = ((c.measureText(label).width + 2.6) * m.scale) / 2;
      const side = effect.target === "opponent" ? 1 : -1;
      const x = Math.max(
        data.safeInsets.left + 16 + halfWidth,
        Math.min(
          this.lastWidth - data.safeInsets.right - 16 - halfWidth,
          head.x + side * this.lastWidth * 0.18,
        ),
      );
      c.shadowColor = "#171125";
      c.shadowBlur = 3;
      c.shadowOffsetY = 2;
      c.translate(x, head.y - 24 - m.rise);
      c.scale(m.scale, m.scale);
      outlinedText(c, label, 0, 0, 28, "#FFD23F", "#171125", 4);
      c.restore();
    }
  }

  drawEnd(t: number, ko: boolean, reduced: boolean): void {
    drawEndOverlay(this.ctx, this.lastWidth, this.lastHeight, t, ko, reduced);
    if (ko) {
      const m = koTimeline(t, reduced);
      if (m.stampAlpha > 0) {
        this.ctx.font = `700 52px ${CANVAS_FONT_FAMILY}`;
        this.ctx.textAlign = "center";
        this.ctx.textBaseline = "middle";
        const metrics = this.ctx.measureText("K.O.!");
        const w =
          metrics.actualBoundingBoxLeft + metrics.actualBoundingBoxRight + 3;
        const h =
          metrics.actualBoundingBoxAscent +
          metrics.actualBoundingBoxDescent +
          3;
        this.bounds.set("hud.ko", {
          x: this.lastWidth / 2 - w / 2,
          y: this.lastHeight * 0.421 - h / 2,
          width: w,
          height: h,
        });
      }
    }
  }

  private drawCooldown(
    x: number,
    y: number,
    size: number,
    progress: number,
    seconds = 0,
  ): void {
    // progress is 0..1, where 1 = full cooldown, 0 = ready
    if (progress <= 0) return;

    const radius = size / 2;
    const centerX = x + radius;
    const centerY = y + radius;
    const face = radius - 2; // inside the 2 px ink outline

    this.ctx.save();
    // Cream face, then the cobalt sweep for the time that is left.
    this.ctx.fillStyle = COOLDOWN_BACKING;
    this.ctx.beginPath();
    this.ctx.arc(centerX, centerY, face, 0, Math.PI * 2);
    this.ctx.fill();
    this.ctx.strokeStyle = COOLDOWN_SWEEP;
    this.ctx.lineWidth = 8;
    this.ctx.lineCap = "butt";
    this.ctx.beginPath();
    this.ctx.arc(
      centerX,
      centerY,
      face - 4,
      -Math.PI / 2,
      -Math.PI / 2 + Math.min(1, progress) * 2 * Math.PI,
    );
    this.ctx.stroke();
    this.ctx.restore();

    // Cooldown seconds: 16 px ink on a cream disc so it reads over the sweep.
    const secondsLeft = seconds.toFixed(1) + "s";
    this.ctx.fillStyle = COOLDOWN_BACKING;
    this.ctx.beginPath();
    this.ctx.arc(centerX, centerY, size * 0.3, 0, Math.PI * 2);
    this.ctx.fill();
    this.ctx.fillStyle = COOLDOWN_INK;
    this.ctx.font = `700 16px ${CANVAS_FONT_FAMILY}`;
    this.ctx.textAlign = "center";
    this.ctx.textBaseline = "middle";
    this.ctx.fillText(`${secondsLeft}`, centerX, centerY);
  }

  drawPauseSheet(
    layoutRects: LayoutRects,
    confirmingForfeit = false,
    round = 1,
    timeLeft = 0,
  ): void {
    const w = this.lastWidth;
    const h = this.lastHeight;

    // Bottom sheet (300pt high)
    const sheetY = Math.max(0, layoutRects.resume.y - 112);
    const sheetH = h - sheetY;
    this.ctx.fillStyle = "#F3EFFF";
    this.ctx.beginPath();
    this.ctx.roundRect(0, sheetY, w, sheetH + 24, [24, 24, 0, 0]);
    this.ctx.fill();

    this.bounds.set("overlay.pauseSheet", {
      x: 0,
      y: sheetY,
      width: w,
      height: sheetH,
    });
    // Title
    this.ctx.fillStyle = "#2A2440";
    this.ctx.font = `600 ${confirmingForfeit ? 20 : 28}px ${CANVAS_FONT_FAMILY}`;
    this.ctx.textAlign = "center";
    this.ctx.textBaseline = "middle";
    this.ctx.fillText(
      confirmingForfeit ? "Forfeit? Counts as a loss" : "PAUSED",
      w / 2,
      sheetY + 48,
    );

    this.ctx.fillStyle = "#7A74B8";
    this.ctx.font = `600 14px ${CANVAS_FONT_FAMILY}`;
    this.ctx.fillText(
      `Round ${round} · ${Math.max(0, Math.ceil(timeLeft))}s left`,
      w / 2,
      sheetY + 78,
    );

    // Resume button (in thumb zone, 56pt from bottom)
    const resume = layoutRects.resume;

    pill(
      this.ctx,
      resume,
      confirmingForfeit ? "#FFFFFF" : "#FFDC46",
      confirmingForfeit ? "#ECEBFF" : "#FFAA1E",
      28,
    );

    this.ctx.fillStyle = "#2A2440";
    this.ctx.font = `600 20px ${CANVAS_FONT_FAMILY}`;
    this.ctx.fillText(
      confirmingForfeit ? "CANCEL" : "RESUME",
      w / 2,
      resume.y + resume.height / 2,
    );

    // Forfeit button (above Resume, 16pt gap)
    const forfeit = layoutRects.forfeit;

    this.ctx.strokeStyle = "#FF5A4E";
    this.ctx.lineWidth = 2;
    this.ctx.fillStyle = "#FFFFFF";
    this.ctx.beginPath();
    this.ctx.roundRect(forfeit.x, forfeit.y, forfeit.width, forfeit.height, 28);
    this.ctx.fill();
    this.ctx.stroke();

    this.ctx.fillStyle = "#2A2440";
    this.ctx.fillText("FORFEIT", w / 2, forfeit.y + forfeit.height / 2);
  }

  resize(width: number, height: number): void {
    this.lastSignature = "";
    // DPR-aware canvas sizing
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    if (
      this.lastWidth === width &&
      this.lastHeight === height &&
      this.lastDPR === dpr
    ) {
      return;
    }
    this.lastWidth = width;
    this.lastHeight = height;
    this.lastDPR = dpr;
    this.ribbonOffsets.clear();

    this.canvas.width = width * dpr;
    this.canvas.height = height * dpr;
    // A retained pixel width can expand the mobile layout viewport on shrink,
    // feeding the previous dimensions back into the next resize event.
    this.canvas.style.width = "100%";
    this.canvas.style.height = "100%";
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }
}
